import React from "react";

const PENDING_DOT_DELAYS = ["0s", "0.16s", "0.32s"] as const;

interface PendingDotsProps {
  className?: string;
}

/**
 * Three staggered dots shown while a request has been pending for a while.
 * Painted in currentColor so it follows the control's own text colour in both
 * light and dark schemes. Decorative only — the control keeps its accessible
 * name from its (visually hidden) content.
 */
export const PendingDots: React.FC<PendingDotsProps> = ({ className }) => {
  const baseClasses = "gcw:inline-flex gcw:items-center gcw:justify-center gcw:gap-[0.2em]";
  const classes = className ? `${baseClasses} ${className}` : baseClasses;
  return (
    <span aria-hidden="true" className={classes}>
      {PENDING_DOT_DELAYS.map((delay) => (
        <span
          key={delay}
          style={{ animationDelay: delay }}
          className="gcw:size-[0.3em] gcw:rounded-full gcw:bg-current gcw:animate-pending-dot gcw:motion-reduce:animate-none"
        />
      ))}
    </span>
  );
};

interface PendingContentProps {
  /** When true, the children stay mounted but are visually replaced by PendingDots. */
  showDots: boolean;
  children: React.ReactNode;
}

/**
 * Stacks the control's content and the pending dots in one grid cell so the
 * control's size never changes when the dots appear. The content fades via
 * opacity (not visibility) so it keeps providing the accessible name. Callers
 * run `usePendingDelay` once per group and pass the result here, so a whole
 * group switches together.
 */
export const PendingContent: React.FC<PendingContentProps> = ({ showDots, children }) => (
  <span className="gcw:inline-grid gcw:place-items-center">
    <span
      className={`gcw:col-start-1 gcw:row-start-1 gcw:inline-flex gcw:items-center gcw:justify-center${
        showDots ? " gcw:opacity-0" : ""
      }`}
    >
      {children}
    </span>
    {showDots && <PendingDots className="gcw:col-start-1 gcw:row-start-1" />}
  </span>
);
